import { useState, useEffect } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  useBlogCategories,
  useCreateBlogCategory,
  useUpdateBlogCategory,
  useDeleteBlogCategory,
  type BlogCategory as Category,
} from "@/hooks/useBlogCategories";

interface CategoryManagerProps {
  /** Rendered inside ContentHub — hides the standalone header. */
  embedded?: boolean;
}

export function CategoryManager({ embedded = false }: CategoryManagerProps) {
  const { data, isLoading: loading, error: loadError } = useBlogCategories();
  const categories = data?.categories ?? [];
  const postCounts = data?.postCounts ?? {};
  const createCategory = useCreateBlogCategory();
  const updateCategory = useUpdateBlogCategory();
  const deleteCategory = useDeleteBlogCategory();
  const saving = createCategory.isPending || updateCategory.isPending;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
  });
  const { toast } = useToast();

  useEffect(() => {
    if (loadError) {
      toast({
        title: "Erro ao carregar categorias",
        description: loadError.message,
        variant: "destructive",
      });
    }
  }, [loadError, toast]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      slug: editingCategory ? formData.slug : generateSlug(name),
    });
  };

  const resetForm = () => {
    setFormData({ name: "", slug: "", description: "" });
    setEditingCategory(null);
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.slug.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Nome e slug são obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    const categoryData = {
      name: formData.name.trim(),
      slug: formData.slug.trim(),
      description: formData.description.trim() || null,
    };

    try {
      if (editingCategory) {
        await updateCategory.mutateAsync({ id: editingCategory.id, input: categoryData });
        toast({ title: "Categoria atualizada!" });
      } else {
        await createCategory.mutateAsync(categoryData);
        toast({ title: "Categoria criada!" });
      }
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({
        title: editingCategory ? "Erro ao atualizar" : "Erro ao criar",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCategory.mutateAsync(id);
      toast({ title: "Categoria excluída!" });
    } catch (error) {
      toast({
        title: "Erro ao excluir",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const dialog = (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button className="h-8 rounded-lg px-[13px] text-xs font-semibold" onClick={openNewDialog}>
          Nova categoria
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editingCategory ? "Editar categoria" : "Nova categoria"}</DialogTitle>
          <DialogDescription>
            {editingCategory
              ? "Atualize as informações da categoria."
              : "Crie uma nova categoria para organizar os posts."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Ex: Desenvolvimento Web"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              placeholder="Ex: desenvolvimento-web"
              className="font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Breve descrição da categoria..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setDialogOpen(false);
              resetForm();
            }}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {editingCategory ? "Salvar" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="flex flex-col gap-[22px]">
      {!embedded && <PageHeader title="Categorias" description="Gerencie as categorias do blog" />}

      <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
        <div className="flex items-center justify-between gap-3 border-b border-border px-[22px] pb-3.5 pt-[18px]">
          <h2 className="text-[15px] font-bold">Categorias do blog</h2>
          {dialog}
        </div>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : categories.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nenhuma categoria encontrada.
          </p>
        ) : (
          categories.map((category) => {
            const n = postCounts[category.id] ?? 0;
            return (
              <div
                key={category.id}
                className="flex items-center gap-4 border-b border-border px-[22px] py-[15px] last:border-b-0 hover:bg-muted/40"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13.5px] font-semibold">{category.name}</div>
                  <div className="mt-0.5 truncate font-mono text-[11.5px] text-muted-foreground">
                    /{category.slug}
                  </div>
                </div>
                <span className="shrink-0 text-[12.5px] text-muted-foreground">
                  {n} {n === 1 ? "post" : "posts"}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-[31px] w-[31px] text-bad hover:text-bad"
                  onClick={() => handleDelete(category.id)}
                  aria-label="Excluir categoria"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  className="h-[31px] rounded-lg px-[13px] text-xs font-semibold"
                  onClick={() => openEditDialog(category)}
                >
                  Editar
                </Button>
              </div>
            );
          })
        )}
      </section>
    </div>
  );
}
